import React from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ArrowRight, ShieldCheck, BellRing, Search, Globe, LayoutDashboard, Sparkles } from "lucide-react";

type WhyItemKey = "monitoring" | "alerts" | "software" | "multilingual" | "security" | "ai";

const ICONS: Record<WhyItemKey, React.ReactNode> = {
  monitoring: <Search className="h-5 w-5" />,
  alerts: <BellRing className="h-5 w-5" />,
  software: <LayoutDashboard className="h-5 w-5" />,
  multilingual: <Globe className="h-5 w-5" />,
  security: <ShieldCheck className="h-5 w-5" />,
  ai: <Sparkles className="h-5 w-5" />,
};

const DEFAULT_LINKS: Record<WhyItemKey, string> = {
  monitoring: "/eu-tender-monitoring",
  alerts: "/ted-procurement-alerts",
  software: "/european-public-procurement-software",
  multilingual: "/multilingual-access",
  security: "/compliance-and-security",
  ai: "/ai-results-analysis",
};

export function WhyTedMonitorSection({ links = DEFAULT_LINKS }: { links?: Partial<Record<WhyItemKey, string>> }) {
  const { t } = useTranslation();

  const items: Array<{ key: WhyItemKey; href: string }> = (Object.keys(ICONS) as WhyItemKey[]).map((key) => ({
    key,
    href: links[key] ?? DEFAULT_LINKS[key],
  }));

  return (
    <section className="mx-auto w-full max-w-6xl px-4 py-10 md:py-14" aria-labelledby="why-tedmonitor-title">
      <div className="flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
        <div className="max-w-2xl">
          {/* Optional micro-label (you already added in it.json; mirror in en.json) */}
          <p className="text-sm font-medium text-muted-foreground">{t("landing.cta.software")}</p>

          <h2 id="why-tedmonitor-title" className="mt-2 text-xl md:text-2xl font-bold tracking-tight">
            {t("landing.why.title")}
          </h2>
          <p className="mt-2 text-sm md:text-base text-muted-foreground">{t("landing.why.subtitle")}</p>
        </div>

        <div className="mt-2 flex flex-col sm:flex-row gap-2 md:mt-0">
          <Button asChild variant="outline" className="gap-2">
            <Link to="/pricing">
              {t("landing.viewPricing")} <ArrowRight className="h-4 w-4" />
            </Link>
          </Button>
          <Button asChild className="gap-2">
            <Link to="/auth">
              {t("landing.getStarted")} <ArrowRight className="h-4 w-4" />
            </Link>
          </Button>
        </div>
      </div>

      <div className="mt-6 grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
        {items.map(({ key, href }) => (
          <Card key={key} className="border-0 shadow-sm bg-card/80 backdrop-blur-sm hover:shadow-md transition-all">
            <CardContent className="p-4">
              <div className="flex items-start gap-3">
                <div className="mt-0.5 inline-flex h-9 w-9 items-center justify-center rounded-lg border bg-background">
                  {ICONS[key]}
                </div>

                <div className="min-w-0">
                  <h3 className="text-sm font-semibold text-foreground leading-snug">
                    {t(`landing.why.items.${key}.title`)}
                  </h3>
                  <p className="mt-1 text-xs text-muted-foreground leading-relaxed">
                    {t(`landing.why.items.${key}.description`)}
                  </p>

                  <div className="mt-3">
                    <Link
                      to={href}
                      className="inline-flex items-center gap-2 text-xs font-medium text-primary hover:underline underline-offset-4"
                      aria-label={t(`landing.why.links.${key}`)}
                    >
                      {t(`landing.why.links.${key}`)} <ArrowRight className="h-3.5 w-3.5" />
                    </Link>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <div className="mt-6 rounded-xl border bg-muted/30 p-4">
        <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
          <div className="max-w-2xl">
            <h3 className="text-sm font-semibold">{t("landing.ctaTitle")}</h3>
            <p className="mt-1 text-xs text-muted-foreground">{t("landing.ctaDescription")}</p>
          </div>
          <Button asChild className="gap-2">
            <Link to="/auth">
              {t("landing.ctaButton")} <ArrowRight className="h-4 w-4" />
            </Link>
          </Button>
        </div>
      </div>
    </section>
  );
}
